import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  UtensilsCrossed,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Phone,
  ArrowRight,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { useCart } from "../App";
import { orderApi, DEFAULT_TABLES } from "../lib/orderApi";
import { businessInfo } from "../data/business";

export default function SelectTable() {
  const navigate = useNavigate();
  const { setTable, tableId: currentTableId } = useCart();

  const [tables, setTables] = useState(DEFAULT_TABLES);
  const [selectedTable, setSelectedTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFallback, setIsFallback] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const fetchTables = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const result = await orderApi.getTables();
      setTables(result.data);
      setIsFallback(result.isFallback);

      // If currentTableId is in storage, pre-select it
      if (currentTableId) {
        const found = result.data.find(
          (t) => String(t.id) === String(currentTableId) || t.name === `Table ${currentTableId}`
        );
        if (found) setSelectedTable(found);
      }
    } catch (err) {
      console.error("Error fetching tables:", err);
      setTables(DEFAULT_TABLES);
      setIsFallback(true);
      setErrorMsg("Backend is waking up or offline. You can still select a table.");
    } finally {
      setLoading(false);
    }
  }, [currentTableId]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const handleSelect = (table) => {
    setSelectedTable(table);
  };

  const handleProceed = () => {
    if (!selectedTable) return;
    setTable(selectedTable.id, selectedTable.name);
    navigate(`/order?table=${selectedTable.id}`);
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9] flex flex-col justify-between py-6 px-4 sm:px-6 max-w-xl mx-auto">
      <div className="space-y-6">
        
        {/* HEADER BRAND */}
        <div className="text-center space-y-2 pt-2">
          <Link to="/" className="inline-flex items-center gap-2 group">
            <img
              src="/logo.png"
              alt="HimCream Naturals"
              className="h-10 w-auto group-hover:scale-105 transition"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <span className="font-extrabold text-xl text-[#2C3E50]">
              HimCream <span className="text-sky-600">Naturals</span>
            </span>
          </Link>
          <p className="text-xs text-stone-500">
            QR Ordering System • Udaipur Store
          </p>
        </div>

        {/* STEP PROGRESS INDICATOR */}
        <div className="bg-white p-3.5 rounded-2xl border border-stone-200/80 shadow-xs">
          <div className="flex items-center justify-between text-[11px] sm:text-xs font-semibold text-stone-500">
            <div className="flex items-center gap-1 text-sky-600 font-bold">
              <span className="w-5 h-5 rounded-full bg-sky-500 text-white flex items-center justify-center text-[10px]">
                1
              </span>
              <span>Select Table</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-stone-300" />
            <div className="flex items-center gap-1 text-stone-400">
              <span className="w-5 h-5 rounded-full bg-stone-100 text-stone-600 flex items-center justify-center text-[10px]">
                2
              </span>
              <span>Browse Menu</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-stone-300" />
            <div className="flex items-center gap-1 text-stone-400">
              <span className="w-5 h-5 rounded-full bg-stone-100 text-stone-600 flex items-center justify-center text-[10px]">
                3
              </span>
              <span>Place Order</span>
            </div>
          </div>
        </div>

        {/* TITLE & NOTICE */}
        <div className="text-center space-y-1">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#2C3E50]">
            Select Your Table
          </h1>
          <p className="text-xs sm:text-sm text-stone-600">
            Choose your table number to start ordering fresh natural ice cream.
          </p>
        </div>

        {/* FALLBACK / OFFLINE NOTICE BANNER */}
        {isFallback && (
          <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2.5 shadow-xs">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">Live backend synchronization running</p>
              <p className="text-amber-700 mt-0.5">
                Tables 1–9 are ready for table ordering. You can order online or inform counter staff.
              </p>
            </div>
            <button
              onClick={fetchTables}
              className="p-1 text-amber-700 hover:bg-amber-100 rounded-lg transition"
              title="Refresh connection"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* TABLE SELECTION GRID */}
        {loading ? (
          <div className="py-12 text-center space-y-3">
            <div className="w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-xs text-stone-500">Checking table availability...</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {tables.map((table) => {
              const isSelected =
                selectedTable && String(selectedTable.id) === String(table.id);

              return (
                <button
                  key={table.id}
                  onClick={() => handleSelect(table)}
                  className={`p-4 rounded-2xl text-center border-2 transition-all duration-200 flex flex-col items-center justify-center gap-1.5 min-h-[95px] ${
                    isSelected
                      ? "border-sky-500 bg-sky-50 shadow-md scale-105"
                      : "border-stone-200 bg-white hover:border-sky-300 hover:bg-stone-50 shadow-xs"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold ${
                      isSelected
                        ? "bg-sky-500 text-white"
                        : "bg-stone-100 text-stone-700"
                    }`}
                  >
                    {table.name.replace(/[^0-9]/g, "") || table.name.slice(0, 2)}
                  </div>
                  <span className="font-bold text-xs sm:text-sm text-[#2C3E50]">
                    {table.name}
                  </span>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      isSelected
                        ? "bg-sky-200/80 text-sky-800"
                        : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {isSelected ? "Selected" : "Available"}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* PROCEED ACTION BUTTON */}
        <div className="pt-2">
          <button
            onClick={handleProceed}
            disabled={!selectedTable}
            className={`w-full py-3.5 px-6 rounded-2xl font-bold text-sm sm:text-base flex items-center justify-center gap-2 shadow-lg transition-all ${
              selectedTable
                ? "bg-sky-500 hover:bg-sky-600 text-white shadow-sky-500/25 hover:scale-[1.02] active:scale-98"
                : "bg-stone-200 text-stone-400 cursor-not-allowed"
            }`}
          >
            <span>
              {selectedTable
                ? `Proceed with ${selectedTable.name}`
                : "Tap a Table to Continue"}
            </span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </div>

      {/* FOOTER & BACK TO WEBSITE */}
      <div className="pt-8 text-center space-y-3">
        <Link
          to="/"
          className="text-xs font-semibold text-stone-500 hover:text-[#2C3E50] transition inline-flex items-center gap-1"
        >
          <span>← Back to HimCream Naturals Website</span>
        </Link>
        <p className="text-[11px] text-stone-400">
          HimCream Naturals • Sec 4, Mali Colony Road, Udaipur
        </p>
      </div>
    </div>
  );
}
